import { type InteractionProfile, profileApi } from '@/features/profile-management';
import { getStorageUrl } from '@/shared/lib';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  MapPin,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface InteractionListProps {
  type: number;
  direction: 'sent' | 'received' | 'bidirectional';
  title: string;
  emptyMessage: string;
}

export function InteractionList({ type, direction, title, emptyMessage }: InteractionListProps) {
  const [page, setPage] = useState(1);
  const count = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['interactionProfiles', type, direction, page],
    queryFn: () => profileApi.getInteractionProfiles(type, direction, page, count),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-9 w-64 mb-2" />
            <Skeleton className="h-5 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Skeleton className="w-16 h-16 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
        </div>
        <Card className="border-destructive/50">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <User className="h-8 w-8 text-destructive" />
            </div>
            <p className="text-destructive font-medium">
              Une erreur est survenue lors du chargement des profils.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const profiles = data?.profiles || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / count);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-1">
            {total} profil{total > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {profiles.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="h-20 w-20 mx-auto bg-primary-surface rounded-full flex items-center justify-center mb-6">
              <User className="h-10 w-10 text-primary" />
            </div>
            <h2 className="font-display text-xl font-semibold mb-2">Aucun profil</h2>
            <p className="text-muted-foreground max-w-sm mx-auto">{emptyMessage}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map((profile) => (
              <ProfileCard key={profile.interactionId} profile={profile} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === page ? 'default' : 'outline'}
                      size="sm"
                      className="w-9 h-9 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProfileCard({ profile }: { profile: InteractionProfile }) {
  const avatarUrl = getStorageUrl(profile.avatarPath);

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Link to={`/profile/${profile.nodeId}`}>
      <Card className="hover:border-primary/30 hover:shadow-md transition-all duration-300 cursor-pointer group">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="relative">
              <Avatar className="w-16 h-16 ring-2 ring-border group-hover:ring-primary/30 transition-all duration-300">
                <AvatarImage src={avatarUrl} alt={profile.username} />
                <AvatarFallback className="text-lg bg-primary text-primary-foreground">
                  {profile.username.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {profile.isPhotoVerified && (
                <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-success rounded-full flex items-center justify-center border-2 border-card">
                  <CheckCircle2 className="h-3 w-3 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold truncate">{profile.username}</h3>
              </div>
              <p className="text-sm text-muted-foreground">{profile.age} ans</p>

              {(profile.city || profile.region) && (
                <div className="flex items-center text-sm text-muted-foreground mt-1.5">
                  <MapPin className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-primary" />
                  <span className="truncate">
                    {[profile.city, profile.region].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}

              {profile.profession && (
                <div className="flex items-center text-sm text-muted-foreground mt-1">
                  <Briefcase className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-accent" />
                  <span className="truncate">{profile.profession}</span>
                </div>
              )}

              <div className="flex items-center text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                <Calendar className="h-3 w-3 mr-1" />
                {formatDate(profile.updatedAt)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
